import type { Message } from '@/context/agent-state.context';

export type CallRole = 'user' | 'assistant';

/** What is still being said on a call, not yet a message. */
export interface LiveSpeech {
  /** What the caller said that the assistant is answering by voice. */
  answered: string;
  /** The assistant's voice-only answer so far, as its voice reaches it. */
  assistant: string;
  /** The caller's current words. */
  user: string;
  /** What the voice says while the agent works on a request ("one sec"). */
  filler: string;
}

/**
 * Transcripts carry non-speech tags ("[mouth noise]", "[laughs]"), and a tag
 * can be cut off mid-stream ("[mouth noise"). Strip both, then tidy spaces.
 */
export function cleanSpeech(text: string): string {
  return text
    .replace(/\[[^\]]*(\]|$)/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.!?;:])/g, '$1')
    .trim();
}

/**
 * Sounds a listener makes while the other side talks ("mhm", "okay"). Being
 * full duplex, GPT-Live makes them mid-sentence: they are not an answer.
 */
const BACKCHANNEL =
  /^(m+-?h+m+|mm+|uh-?huh|okay|ok|right|yeah|yes|sure|got it|i see)[.!,]?$/i;

/** Enough of what was said last to see how its last sentence ends. */
const LAST_SAID_CHARS = 200;

/** Only a listener's sound ("mhm", "okay"), not something said. */
export function isBackchannel(text: string): boolean {
  return BACKCHANNEL.test(cleanSpeech(text));
}

/**
 * Sorts a call's speech into thread messages.
 *
 * A request the assistant delegates becomes an ordinary chat-agent turn, so
 * its reply is the agent's own message. Everything else ("hello" → "hi
 * Eric") is a voice-only exchange, emitted turn by turn so it can be saved
 * to the thread in order. Filler spoken while a delegated request runs ("one
 * moment") is shown while the agent works, never saved; the read-out of the
 * reply is the agent's message already.
 */
export class CallTurns {
  /**
   * `voice`: the assistant is answering by voice; `delegated`: it handed a
   * request to the agent and what it says is filler; `replied`: the agent's
   * reply was handed back, so the caller's next words start a new exchange.
   */
  private phase: 'voice' | 'delegated' | 'replied' = 'voice';
  private assistantText = '';
  private fillerText = '';
  /**
   * The end of whatever the assistant said last, in any phase (a voice
   * answer, filler, or the read-out of the agent's reply): what a caller's
   * "yes" would be answering.
   */
  private lastSaid = '';

  constructor(private readonly emit: (role: CallRole, text: string) => void) {}

  /**
   * The caller started a new turn, so the last exchange is settled:
   * `answered` is what they said that the assistant answered by voice, saved
   * first, then the assistant's answer.
   */
  userSpoke(answered: string): void {
    this.fillerText = '';
    if (answered) this.emit('user', answered);
    this.finishAssistantTurn();
    if (this.phase === 'replied') this.phase = 'voice';
  }

  /**
   * The caller spoke again. If what the assistant said since was only a
   * backchannel ("mhm"), they are still mid-turn: keep their words together.
   * Otherwise the last exchange is settled (see {@link userSpoke}).
   */
  callerSpoke(buffer: {
    commitAnswered: () => string;
    cancelAnswered: () => void;
  }): void {
    if (this.phase === 'voice' && this.onlyBackchannel()) {
      this.assistantText = '';
      buffer.cancelAnswered();
      return;
    }
    this.userSpoke(buffer.commitAnswered());
  }

  private onlyBackchannel(): boolean {
    return (
      !cleanSpeech(this.assistantText) || isBackchannel(this.assistantText)
    );
  }

  /** The assistant's last words end in a question. */
  askedQuestion(): boolean {
    // A closing quote or bracket may follow the question mark.
    return /\?["'”’)\]]*$/.test(cleanSpeech(this.lastSaid));
  }

  /**
   * The caller talked over the assistant and it stopped mid-sentence: its
   * answer so far ends in a dash, so it reads as cut off, not finished.
   */
  cutOff(): void {
    const said = cleanSpeech(this.assistantText);
    // A listener's "mhm" is not an answer to cut off.
    if (!said || isBackchannel(said) || /[.!?…]$/.test(said)) return;
    this.assistantText = `${said}—`;
  }

  /**
   * GPT-Live delegated. What it was saying ("sure, one sec") was filler for
   * this request, not an answer. What it says next is filler too, until the
   * reply's read-out.
   */
  delegationStarted(): void {
    this.fillerText = this.assistantText;
    this.assistantText = '';
    this.phase = 'delegated';
  }

  /** The agent's reply was handed to GPT-Live to speak. */
  replyDelivered(): void {
    if (this.phase === 'delegated') this.phase = 'replied';
  }

  /** Words the assistant has now actually said (released in time with audio). */
  assistantSpoke(delta: string): void {
    this.lastSaid = (this.lastSaid + delta).slice(-LAST_SAID_CHARS);
    if (this.phase === 'voice') this.assistantText += delta;
    else if (this.phase === 'delegated') this.fillerText += delta;
  }

  /**
   * The assistant is about to speak unprompted (the idle warning): the
   * exchange before it is settled, in order (see {@link userSpoke}), so the
   * warning is a turn of its own, not the end of the last answer.
   */
  settle(answered: string): void {
    this.userSpoke(answered);
  }

  /** Hang-up: settle the last exchange. */
  end(answered: string): void {
    this.userSpoke(answered);
    this.phase = 'voice';
  }

  /** What the voice said while the agent worked on the last request. */
  liveFiller(): string {
    return cleanSpeech(this.fillerText);
  }

  /** What the assistant is saying by voice alone, as far as it has said it. */
  liveAssistant(): string {
    return cleanSpeech(this.assistantText);
  }

  private finishAssistantTurn(): void {
    const text = cleanSpeech(this.assistantText);
    this.assistantText = '';
    if (text) this.emit('assistant', text);
  }
}

/**
 * The thread as the panel shows it during a call, with whatever is still
 * being said as live bubbles that become real messages once each turn is
 * done.
 */
export function withLiveCall(
  messages: readonly Message[],
  live: LiveSpeech,
): Message[] {
  const shown = [...messages];
  const bubble = (role: CallRole, content: string): Message => ({
    id: `live-voice-${role}-${shown.length}`,
    role,
    content,
    timestamp: new Date(),
    channel: 'voice',
  });
  if (live.answered) shown.push(bubble('user', live.answered));
  if (live.assistant) shown.push(bubble('assistant', live.assistant));
  if (live.user) shown.push(bubble('user', live.user));
  return shown;
}
